import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { useTheme } from "@/components/feature/theme";

const PAGES_PER_CHAPTER = 4;

const readerImage =
  "https://readdy.ai/api/search-image?query=Black%20and%20white%20japanese%20manga%20story%20page%20with%20multiple%20cinematic%20panels%20showing%20dynamic%20action%20and%20dialogue%20scenes%2C%20screentone%20shading%2C%20detailed%20clean%20lineart%2C%20dramatic%20composition%2C%20ink%20illustration%20style&width=800&height=1200&seq=reader-page-01&orientation=portrait";

export default function Reader() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const comic = comics.find((c) => c.id === id);
  const { isDark, toggle } = useTheme();

  const initChapter = Math.min(
    Math.max((Number(searchParams.get("chapter")) || 1) - 1, 0),
    (comic?.chapters.length || 1) - 1,
  );
  const [chapterIndex, setChapterIndex] = useState(initChapter);
  const [pageIndex, setPageIndex] = useState(0);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [collected, setCollected] = useState(false);

  const totalPages = PAGES_PER_CHAPTER;
  const totalChapters = comic?.chapters.length || 0;

  // sync reading record
  useEffect(() => {
    if (!id) return;
    try {
      window.localStorage.setItem(`cechi-read-${id}`, String(chapterIndex + 1));
    } catch {
      /* ignore */
    }
  }, [id, chapterIndex]);

  const goChapter = (i: number) => {
    const next = Math.min(Math.max(i, 0), totalChapters - 1);
    setChapterIndex(next);
    setPageIndex(0);
    setSearchParams({ chapter: String(next + 1) }, { replace: true });
    setCatalogOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextPage = () => {
    if (pageIndex + 1 < totalPages) {
      setPageIndex(pageIndex + 1);
    } else if (chapterIndex + 1 < totalChapters) {
      goChapter(chapterIndex + 1);
    }
  };

  const prevPage = () => {
    if (pageIndex > 0) {
      setPageIndex(pageIndex - 1);
    } else if (chapterIndex > 0) {
      goChapter(chapterIndex - 1);
      setPageIndex(PAGES_PER_CHAPTER - 1);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") nextPage();
      if (e.key === "ArrowLeft") prevPage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, chapterIndex]);

  const chapterLabel = comic?.chapters[chapterIndex] || "第1话";
  const progress = useMemo(
    () => ((chapterIndex * totalPages + pageIndex + 1) / (totalChapters * totalPages)) * 100,
    [chapterIndex, pageIndex, totalChapters, totalPages],
  );

  if (!comic) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50">
        <div className="text-center">
          <p className="text-sm text-foreground-600">未找到该漫画</p>
          <Link to="/category" className="mt-4 inline-block rounded-full bg-primary-600 px-6 py-2.5 text-sm text-background-50">
            返回分类
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b border-background-200/70 bg-background-50">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between gap-3 px-4 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to={`/comic/${comic.id}`}
              aria-label="返回详情"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-200/70 text-foreground-700 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
            </Link>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-foreground-950">{comic.title}</p>
              <p className="truncate text-xs text-foreground-500">
                {chapterLabel} · 第{pageIndex + 1}/{totalPages}页
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setCollected((v) => !v)}
              aria-label="收藏"
              className={`flex h-9 w-9 items-center justify-center rounded-full ${
                collected ? "bg-accent-500 text-background-50" : "bg-background-200/70 text-foreground-700 hover:text-accent-500"
              }`}
            >
              <i className={collected ? "ri-star-fill" : "ri-star-line"}></i>
            </button>
            <button
              type="button"
              onClick={toggle}
              aria-label="夜间模式"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200/70 text-foreground-700 hover:text-accent-500"
            >
              <i className={isDark ? "ri-sun-line" : "ri-moon-line"}></i>
            </button>
            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              className="flex h-9 items-center gap-1 whitespace-nowrap rounded-full bg-primary-600 px-3 text-sm text-background-50 hover:bg-primary-700"
            >
              <i className="ri-list-unordered"></i>
              目录
            </button>
          </div>
        </div>
        <div className="h-0.5 w-full bg-background-200/70">
          <div className="h-full bg-accent-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      {/* Reader content */}
      <main className="mx-auto max-w-3xl px-3 py-5 md:px-6">
        <div className="text-center">
          <p className="font-heading text-lg text-foreground-950">{chapterLabel}</p>
          <p className="mt-1 text-xs text-foreground-500">点击左右箭头或键盘 ← → 翻页</p>
        </div>

        <div className="mt-4 flex items-center justify-center">
          <img
            src={readerImage}
            alt={`${comic.title} ${chapterLabel} 漫画页`}
            className="w-full max-w-xl rounded-xl border border-background-200/70"
          />
        </div>

        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            disabled={pageIndex === 0 && chapterIndex === 0}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-foreground-700 shadow-sm hover:text-primary-600 disabled:opacity-40"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <span className="rounded-full bg-background-50 px-4 py-2 text-xs text-foreground-600 shadow-sm">
            {pageIndex + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
            disabled={pageIndex === totalPages - 1 && chapterIndex === totalChapters - 1}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-foreground-700 shadow-sm hover:text-primary-600 disabled:opacity-40"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>

        {/* prev/next chapter */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={prevPage}
            disabled={chapterIndex === 0}
            className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-700 disabled:opacity-40"
          >
            <span className="block text-xs text-foreground-500">上一章</span>
            {chapterIndex > 0 ? comic.chapters[chapterIndex - 1] : "已是第一章"}
          </button>
          <button
            type="button"
            onClick={nextPage}
            disabled={chapterIndex >= totalChapters - 1}
            className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-700 disabled:opacity-40"
          >
            <span className="block text-xs text-foreground-500">下一章</span>
            {chapterIndex < totalChapters - 1 ? comic.chapters[chapterIndex + 1] : "已是最后一章"}
          </button>
        </div>
      </main>

      {/* Chapter catalog drawer */}
      {catalogOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-background-950/40 backdrop-blur-sm" onClick={() => setCatalogOpen(false)}></div>
          <aside className="h-full w-80 overflow-y-auto border-l border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-list-unordered text-accent-500"></i>
                章节目录
              </h2>
              <button
                type="button"
                onClick={() => setCatalogOpen(false)}
                aria-label="关闭目录"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200/70 text-foreground-700"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="mt-1 text-xs text-foreground-500">{comic.title} · 共{totalChapters}话</p>
            <div className="mt-4 flex flex-col gap-1.5">
              {comic.chapters.map((ch, i) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => goChapter(i)}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    i === chapterIndex
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-200/70"
                  }`}
                >
                  <span className="truncate">{ch}</span>
                  {i === chapterIndex && <i className="ri-check-line shrink-0"></i>}
                </button>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}